import { LegalLayout } from "@/components/LegalLayout";
import { CONTACT_EMAIL, CHILD_SAFETY_EMAIL } from "@/constants/site";

export default function ChildSafety() {
  const safetyEmail = (
    <a href={`mailto:${CHILD_SAFETY_EMAIL}`} data-testid="child-safety-email">
      {CHILD_SAFETY_EMAIL}
    </a>
  );
  const supportEmail = <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;

  return (
    <LegalLayout
      testId="child-safety-page"
      eyebrow="Trust & Safety"
      title="Aura Live! — Child Safety Standards"
      updated="October 3, 2026"
      intro="Aura Live! has zero tolerance for child sexual abuse and exploitation. This page sets out our prohibited-conduct standards, how to report child-safety concerns, and how our moderation team acts on them."
    >
      <p>
        Aura Live! (package <code>com.auralive.app</code>) is an adults-only
        (18+) live-streaming service operated by Mapcast BV, Amsterdam, The
        Netherlands. Protecting children is our highest safety priority. These
        Child Safety Standards apply to every user, broadcast, message, upload,
        sound, and piece of content on the service, and are incorporated into our{" "}
        <a href="/terms">Terms of Service</a> and{" "}
        <a href="/safety">Community Guidelines</a>. This policy is effective as
        of 3 October 2026.
      </p>

      <h2>Zero tolerance for child sexual abuse and exploitation (CSAE)</h2>
      <p>
        We strictly prohibit, and have zero tolerance for, child sexual abuse
        and exploitation (CSAE) in any form, anywhere on Aura Live!. This
        expressly includes, without limitation:
      </p>
      <ul>
        <li><strong>Grooming</strong> — building a relationship with, or contacting, a minor to enable sexual abuse or exploitation.</li>
        <li><strong>Sexual exploitation of minors</strong> — any use of a minor for sexual gratification, sexual activity, or sexual performance.</li>
        <li><strong>Sextortion</strong> — threatening or blackmailing a minor (or any person using child imagery) using sexual content.</li>
        <li><strong>Child trafficking</strong> — recruiting, advertising, soliciting, or facilitating the trafficking of a minor.</li>
        <li><strong>Sexualization of minors</strong> — sexualizing, or depicting in a sexualized manner, any person who is, or appears to be, a minor, including through text, imagery, audio, AI-generated media, or edited/animated content.</li>
        <li>Any attempt to make contact with a minor for the purposes above, or to normalize, promote, or provide instructions for any of the above.</li>
      </ul>

      <h2>No child sexual abuse material (CSAM)</h2>
      <p>
        You must never create, upload, stream, share, link to, request, or
        distribute child sexual abuse material (CSAM) through Aura Live!. This
        prohibition covers real, AI-generated, animated, drawn, and edited
        imagery or audio, and any content that sexualizes a person who is, or
        appears to be, under 18. There are no exceptions.
      </p>

      <h2>Age requirement</h2>
      <p>
        Aura Live! is intended solely for adults aged 18 or older. We collect
        date of birth at first sign-in to refuse under-18 accounts, and we do
        not knowingly allow minors on the service. If we learn that a minor has
        created an account, we remove the account and associated data. Minors
        must never appear as a recognizable subject of a broadcast.
      </p>

      <h2>How to report a child-safety concern</h2>
      <p>
        We provide verified, working reporting methods. Reports can be made
        without logging in, and we encourage anyone — users and non-users — to
        report immediately:
      </p>
      <ul>
        <li><strong>In-app report tools:</strong> every live stream, profile, post, chat, and sound has a <em>Report</em> control. Reports capture the target, a reason, and optional details, and are sent directly to our moderation queue.</li>
        <li><strong>In-app block tools:</strong> you can block and unblock any user at any time to stop all contact from them.</li>
        <li><strong>Dedicated child-safety contact (monitored):</strong> email {safetyEmail}. Use this address specifically for child-safety concerns, CSAE, or suspected CSAM. For all other matters, contact {supportEmail}.</li>
      </ul>
      <p>
        If a child is in immediate danger, contact your local emergency
        services and law-enforcement authorities first, then notify us at{" "}
        {safetyEmail}.
      </p>

      <h2>How our moderation team handles child-safety reports</h2>
      <p>
        Child-safety reports are prioritized. Our process, as operated today,
        is:
      </p>
      <ul>
        <li><strong>Triage:</strong> reports land in a dedicated moderation review queue where child-safety reports are escalated ahead of other categories.</li>
        <li><strong>Review:</strong> trained reviewers assess the reported account, stream, or content. Reviewers can immediately force-end a live broadcast that is in progress.</li>
        <li><strong>AI assistance:</strong> real-time AI text moderation screens chat and text for abusive and prohibited content and flags it for review.</li>
        <li><strong>Resolution:</strong> each report is actioned and recorded with a resolution note. Enforcement actions are written to an internal audit log.</li>
      </ul>

      <h2>Removal, enforcement, and reporting to authorities</h2>
      <p>When child-safety abuse is confirmed, we act decisively:</p>
      <ul>
        <li><strong>Live broadcasts:</strong> an administrator can immediately force-end any live broadcast that is in progress.</li>
        <li><strong>Replays and clips:</strong> saved replays and clips can be deleted and removed from the service.</li>
        <li><strong>Account enforcement:</strong> the responsible account is banned. CSAE/CSAM violations result in permanent removal with no appeal, forfeiture of any accumulated coins, and preservation of relevant records as required by law.</li>
        <li><strong>Reporting to authorities:</strong> where we identify confirmed CSAM or a credible child-safety threat, our moderation team reports it to the appropriate authority — including, where applicable, the U.S. National Center for Missing &amp; Exploited Children (NCMEC) and/or the relevant local law-enforcement or hotline — and preserves associated data, in accordance with applicable law. This reporting is currently carried out as a manual operational process by our team rather than an automated pipeline.</li>
      </ul>

      <h2>Scope and current limitations (transparency)</h2>
      <p>
        We believe in being accurate about what is operational today. In the
        interest of transparency:
      </p>
      <ul>
        <li>Direct takedown controls today cover <strong>live broadcasts</strong> (force-end), <strong>replays</strong>, and <strong>clips</strong> (delete). There is <strong>no dedicated administrator control to remove an individual feed post or a standalone uploaded image</strong>; those cases are handled by banning the responsible account and manual removal by our operations team. A direct post/image takedown control is on our roadmap.</li>
        <li>Reporting to NCMEC and/or local authorities is performed <strong>manually</strong> by the moderation team; we do not yet operate an automated authority-reporting pipeline.</li>
        <li>Automated content scanning is applied to <strong>text</strong> (real-time AI text moderation). We do not currently run automated image/video hash-matching (for example PhotoDNA or CSAI Match); image, video, and live content are actioned through user reports and human review.</li>
        <li>Child-safety reports today use the general in-app report flow (target, reason, details) routed to the moderation queue and our dedicated child-safety contact; a one-tap dedicated "child safety" report category is on our roadmap.</li>
      </ul>
      <p>
        We continue to invest in these controls and will update this page as
        our operational procedures evolve.
      </p>

      <h2>Designated child-safety contact</h2>
      <p>
        Our monitored point of contact for all child-safety matters, including
        questions about these standards, is {safetyEmail}. You can also write to
        us by post at:
      </p>
      <p>
        <strong>Mapcast BV</strong><br />
        Child Safety Team<br />
        Herengracht 320<br />
        1016 CE Amsterdam, The Netherlands
      </p>
    </LegalLayout>
  );
}
